import React from "react";
import { Box, TextField, useTheme } from "@mui/material";
import { Formik, Form } from "formik";
import * as yup from "yup";
import { DataGrid, GridActionsCellItem } from "@mui/x-data-grid";
import DeleteOutlinedIcon from "@mui/icons-material/DeleteOutlined";
import { dataGridStyles, localeText } from "../utils/dataGridConfig";
import { tokens } from "../theme";
import { useCreateBlockMutation, useDeleteBlockMutation } from "../services/blockService";
import SuccessButton from "./SuccessButton";
import { useAlert } from "../global/AlertContext";

const BlocksList = ({ lote, refetchLote, isReadOnly = false }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const showAlert = useAlert();

  // Mutation hooks for creating and deleting blocks
  const [createBlock] = useCreateBlockMutation();
  const [deleteBlock] = useDeleteBlockMutation();

  // Validation schema for the block form
const blockSchema = yup.object().shape({
  number: yup.string().required("El número de bloque es requerido"),
  plants: yup
    .number()
    .typeError("Debe ser un número válido")
    .required("La cantidad de plantas es requerido")
    .integer("Debe ser un número entero (sin decimales)")
    .min(0, "La cantidad no puede ser negativa"),
  area: yup
    .number()
    .typeError("Debe ser un número válido")
    .required("El área es requerido")
    .positive("El área debe ser un número positivo"),
});

  const allBlocks = lote?.blocks || [];
  const baseBlockIds = new Set(lote?.baseBlocks?.map(block => block.id));

  const blocksList = allBlocks.map(block => {
      return {
          ...block,
          is_forced: !baseBlockIds.has(block.id) // Set is_forced to true if the block is not in baseBlocks
      };
  });

  // Function to handle the deletion of a block
  const handleDeleteBlock = async (id) => {
    try {
      await deleteBlock(id).unwrap();
      refetchLote(); // Refetch the list of blocks after deletion
      showAlert("Bloque Borrado", "success");
    } catch (error) {
      showAlert(error.message, "error");
    }
  };

  // Function to handle adding a new block
  const handleCreateBlock = async (values, { resetForm }) => {
    try {
      await createBlock( { ...values, lote_id: lote.id, sowing_date: lote.sowing_date } ).unwrap();
      refetchLote(); // Refetch the list of blocks after creation
      resetForm(); // Reset the form after successful submission
      showAlert("Bloque Creado", "success");
    } catch (error) {
      showAlert(error.message, "error");
    }
  };

  // Define columns for the DataGrid
  const columns = [
    {
      field: "number",
      headerName: "Número",
      flex: 1,
      cellClassName: "name-column--cell",
    },
    {
      field: "plants",
      headerName: "Plantas",
      type: "string",
      headerAlign: "left",
      align: "left",
      flex: 1,
    },
    {
      field: "area",
      headerName: "Área",
      type: "number",
      headerAlign: "left",
      align: "left",
      flex: 1,
    },
    {
      field: "is_forced",
      headerName: "Forzado",
      type: "string",
      headerAlign: "center",
      align: "center",
      flex: 1,
      renderCell: (params) => {
        console.log(params);
        const value = params.row.is_forced ? "Sí" : "No";
        const color = value==="Sí" ? "#4cceac" : "#ea3329";
  
        return (
        <Box
          sx={{
            color: color,
            fontWeight: "bold",
            backgroundColor: `${color}30`, // Transparent background
            border: ` ${color}`, // Corner borders
            borderRadius: "8px", // Rounded corners
            padding: "8px",
            display: "inline-flex",
            justifyContent: "center",
            alignItems: "center",
            width: "10%",
            height: "50%",
            textShadow: `0 0 30px ${color}`, // Shiny text effect
            overflow: "hidden", // Prevent shadow from going outside the box
          }}
        >
          {value}
        </Box>
        );
      },
    },
  ];

  // Add the "Actions" column only if isReadOnly is false
  if (!isReadOnly) {
    columns.push({
      field: "actions",
      type: "actions",
      headerName: "Acciones",
      flex: 1,
      getActions: (params) => [
        <GridActionsCellItem
          icon={<DeleteOutlinedIcon />}
          label="Delete"
          onClick={() => handleDeleteBlock(params.id)}
        />,
      ],
    });
  }

  return (
    <Box>
      {/* Form to add a new block */}
      {!isReadOnly && (
        <Formik
          initialValues={{ number: "", plants: "", area: "" }}
          validationSchema={blockSchema}
          onSubmit={handleCreateBlock}
        >
          {({ values, errors, touched, handleBlur, handleChange, handleSubmit }) => (
            <Form onSubmit={handleSubmit}>
              <Box
                display="flex"
                gap="10px"
                alignItems="center"
              >
                <TextField
                  fullWidth
                  variant="filled"
                  type="text"
                  label="Número"
                  onBlur={handleBlur}
                  onChange={handleChange}
                  value={values.number}
                  name="number"
                  error={!!touched.number && !!errors.number}
                  helperText={touched.number && errors.number}
                />
                <TextField
                  fullWidth
                  variant="filled"
                  type="number"
                  label="Plantas"
                  onBlur={handleBlur}
                  onChange={handleChange}
                  value={values.plants}
                  name="plants"
                  error={!!touched.plants && !!errors.plants}
                  helperText={touched.plants && errors.plants}
                  slotProps={{
                    htmlInput: {
                      step: "1",  // Only whole numbers
                      min: "0"    // No negative numbers
                    }
                  }}
                />
                <TextField
                  fullWidth
                  variant="filled"
                  type="number"
                  label="Área"
                  onBlur={handleBlur}
                  onChange={handleChange}
                  value={values.area}
                  name="area"
                  error={!!touched.area && !!errors.area}
                  helperText={touched.area && errors.area}
                />
                <Box
                  textAlign="center"
                >
                  <SuccessButton label="Crear" />
                </Box>
              </Box>
            </Form>
          )}
        </Formik>
      )}

      {/* DataGrid to display blocks */}
      <Box height="50vh" sx={dataGridStyles(colors)}>
        <DataGrid
          rows={blocksList}
          columns={columns}
          getRowId={(row) => row.id}
          pageSizeOptions={[100]}
          loadingText="Cargando..."
          localeText={localeText}
          disableRowSelectionOnClick
          disableColumnMenu
          disableColumnSorting
        />
      </Box>
    </Box>
  );
};

export default BlocksList;